/**
 * The Review screen's emails card (`.email-status`, YAR-553): which emails
 * Yaruta is still reading and which it couldn't read, and what the card's
 * header says about them. Pure — no React, no clock, no Supabase — so the
 * copy and ordering are unit tests.
 *
 * Rules it encodes:
 * - "Reading" is `isReadingStatus` (RECEIVED, PROCESSING, HELD); "failed" is
 *   the server's verdict, status ERROR. Nothing else is on the card.
 * - The card shows only when something is reading or failed (AC1).
 * - Reading turns into "Still reading" once any reading email has been at it
 *   for STILL_READING_AFTER_MS — the same threshold as the status toast. Only
 *   the wording changes; the give-up is the server's timeout.
 * - Rows: failed first (they need the parent), then reading, each oldest
 *   first.
 * - Sender is the ingestion label, never an address (AC8). No name → empty.
 * - A photo or file the parent added (YAR-556) is "Added by you". A line
 *   whose rows are exactly one upload speaks of "your photo" / "your file"
 *   (Figma Review Flow v2.0, photo being read / couldn't read); several, or
 *   a mix with emails, keep the email wording (D12, as the status toast).
 */

import type { EmailStatusFeedRow } from '@/services/email.service';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import { isReadingStatus } from '@/lib/emailReadState';
import type { UploadKind } from '@/lib/uploadKind';
import {
  STILL_READING_AFTER_MS,
  countNoun,
  senderLine,
} from '@/lib/emailStatusRun';

/** B1 owns these strings; `REVIEW_COPY` (reviewCopy.ts) spreads them. */
export const EMAILS_CARD_COPY = {
  emailsReading: (n: number) => `Reading ${countNoun(n, 'email', 'emails')}`,
  emailsStillReading: (n: number) =>
    `Still reading ${countNoun(n, 'email', 'emails')}`,
  emailsFailed: (n: number) =>
    `${countNoun(n, 'email', 'emails')} couldn't be read`,
  /** One upload (YAR-556); the typographic apostrophe as Figma draws it. */
  uploadReading: (kind: UploadKind) => `Reading your ${kind}`,
  uploadStillReading: (kind: UploadKind) => `Still reading your ${kind}`,
  uploadFailed: (kind: UploadKind) => `Your ${kind} couldn’t be read`,
} as const;

/** The kind when `rows` is exactly one upload; null otherwise (D12). */
function loneUpload(rows: readonly EmailStatusFeedRow[]): UploadKind | null {
  if (rows.length !== 1 || rows[0].source !== 'upload') return null;
  return rows[0].uploadKind === 'file' ? 'file' : 'photo';
}

/** Row title when the email has no subject (same as today's Review). */
const NO_SUBJECT = '(no subject)';

export interface EmailsCardRow {
  id: string;
  subject: string;
  /** Sender name, or '' when there is none to show (never an address). */
  sender: string;
  state: 'reading' | 'failed';
  failureCount: number;
}

export interface EmailsCardModel {
  /** reading + failed > 0 (AC1). */
  visible: boolean;
  readingCount: number;
  /** Any reading email has been at it for STILL_READING_AFTER_MS or more. */
  stillReading: boolean;
  failedCount: number;
  /** 'Reading [N] emails' | 'Still reading [N] emails', null when none. */
  readingLine: string | null;
  /** "[n] email(s) couldn't be read", null when none. */
  failedLine: string | null;
  /** Failed first, then reading, each oldest first. */
  rows: EmailsCardRow[];
}

function time(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

function byArrival(a: EmailStatusFeedRow, b: EmailStatusFeedRow): number {
  return time(a.createdAt) - time(b.createdAt);
}

function toRow(
  email: EmailStatusFeedRow,
  state: EmailsCardRow['state'],
): EmailsCardRow {
  const subject = email.subject?.trim();
  return {
    id: email.id,
    subject: subject ? subject : NO_SUBJECT,
    // Never the uploader's address (YAR-556).
    sender:
      email.source === 'upload'
        ? ADD_TO_YARUTA_COPY.addedByYou
        : (senderLine([email.senderLabel]) ?? ''),
    state,
    failureCount: email.failureCount,
  };
}

export function emailsCardModel(
  emails: readonly EmailStatusFeedRow[],
  now: number,
): EmailsCardModel {
  const reading = emails
    .filter((e) => isReadingStatus(e.status))
    .sort(byArrival);
  const failed = emails.filter((e) => e.status === 'ERROR').sort(byArrival);

  const stillReading = reading.some((e) => {
    const started = time(e.readingStartedAt ?? e.createdAt);
    return started > 0 && now - started >= STILL_READING_AFTER_MS;
  });

  const readingUpload = loneUpload(reading);
  const failedUpload = loneUpload(failed);
  const readingLine =
    reading.length === 0
      ? null
      : readingUpload
        ? stillReading
          ? EMAILS_CARD_COPY.uploadStillReading(readingUpload)
          : EMAILS_CARD_COPY.uploadReading(readingUpload)
        : stillReading
          ? EMAILS_CARD_COPY.emailsStillReading(reading.length)
          : EMAILS_CARD_COPY.emailsReading(reading.length);

  return {
    visible: reading.length + failed.length > 0,
    readingCount: reading.length,
    stillReading,
    failedCount: failed.length,
    readingLine,
    failedLine:
      failed.length === 0
        ? null
        : failedUpload
          ? EMAILS_CARD_COPY.uploadFailed(failedUpload)
          : EMAILS_CARD_COPY.emailsFailed(failed.length),
    rows: [
      ...failed.map((e) => toRow(e, 'failed')),
      ...reading.map((e) => toRow(e, 'reading')),
    ],
  };
}
